import { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import type { AlertColor } from "@mui/material/Alert";
import { Toast } from "@/components/atoms/Toast";

interface ToastState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

interface ToastContextValue {
  showToast: (message: string, severity?: AlertColor) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const INITIAL_STATE: ToastState = {
  open: false,
  message: "",
  severity: "info",
};

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * ToastProvider - Provides toast notification functionality to the app
 * Wrap your app with this provider to enable useToast hook
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toast, setToast] = useState<ToastState>(INITIAL_STATE);

  const handleClose = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const showToast = useCallback(
    (message: string, severity: AlertColor = "info") => {
      setToast({ open: true, message, severity });
    },
    [],
  );

  const showSuccess = useCallback(
    (message: string) => {
      showToast(message, "success");
    },
    [showToast],
  );

  const showError = useCallback(
    (message: string) => {
      showToast(message, "error");
    },
    [showToast],
  );

  const showWarning = useCallback(
    (message: string) => {
      showToast(message, "warning");
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message: string) => {
      showToast(message, "info");
    },
    [showToast],
  );

  const value: ToastContextValue = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleClose}
      />
    </ToastContext.Provider>
  );
}

/**
 * useToast - Hook to access toast notification functions
 * Must be used within a ToastProvider
 *
 * @example
 * const { showError, showSuccess } = useToast();
 * showError("This email is already registered");
 * showSuccess("Contact created successfully");
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
